import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { shouldOfferDesktopRelaunch } from './desktopUpdatePresentation';
import { useDesktopUpdateState } from './useDesktopUpdateState';

interface DesktopUpdateRailActionProps {
  expanded: boolean;
}

export default function DesktopUpdateRailAction({ expanded }: DesktopUpdateRailActionProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const state = useDesktopUpdateState();
  const [restarting, setRestarting] = useState(false);

  if (!shouldOfferDesktopRelaunch(state)) return null;

  const relaunch = async () => {
    if (restarting) return;
    setRestarting(true);
    try {
      await window.edgelexDesktop!.updates.installAndRestart();
    } catch {
      setRestarting(false);
      navigate('/update');
    }
  };

  return (
    <button
      type="button"
      onClick={() => void relaunch()}
      disabled={restarting}
      className={`relative flex w-full items-center rounded-lg border border-[var(--lex-accent-primary)] bg-[var(--lex-accent-primary-soft)] text-[var(--lex-accent-deep)] shadow-sm transition-colors hover:bg-[var(--lex-surface-hover)] disabled:opacity-70 ${
        expanded ? 'gap-3 px-3 py-2.5 text-left' : 'justify-center px-2 py-2.5'
      }`}
      title={!expanded ? 'Relaunch with Update' : undefined}
      aria-label="Relaunch EdgeLex with the downloaded update"
    >
      <ArrowPathIcon className={`h-5 w-5 shrink-0 ${restarting ? 'animate-spin' : ''}`} />
      {expanded && (
        <span className="min-w-0">
          <span className="block text-sm font-semibold">{restarting ? 'Restarting EdgeLex…' : 'Relaunch with Update'}</span>
          <span className="block truncate text-[11px] text-[var(--lex-text-secondary)]">
            EdgeLex {state.availableVersion || 'update'} is ready
          </span>
        </span>
      )}
      {!expanded && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[var(--lex-status-success)]" />}
    </button>
  );
}
